import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { AirQualityService } from "../../services/airQualityService";
import { getLocationsApi, getLocationDetailApi } from "../../lib/api/location";
import { getDeviceListApi } from "../../lib/api/device";
import "./Stat.css";

const StatLocationView = () => {
  // 사업장 선택 상태
  const [selectedBusiness, setSelectedBusiness] = useState("");
  const [selectedSensor, setSelectedSensor] = useState("종합");

  // 차트 데이터
  const [chartData, setChartData] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState("복합악취");
  // 기본 날짜를 어제~오늘로 설정
  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };
  
  const getToday = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const [startDate, setStartDate] = useState(getYesterday());
  const [endDate, setEndDate] = useState(getToday());
  const [loading, setLoading] = useState(false);
  const [businessData, setBusinessData] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [selectedLocationData, setSelectedLocationData] = useState(null);

  const airQualityService = AirQualityService.getInstance();

  // 사업장 목록 로드
  const loadAvailableLocations = async () => {
    try {
      const locationResponse = await getLocationsApi({
        size: 100,
        page: 0
      });
      
      setAvailableLocations(locationResponse.content);
      if (locationResponse.content.length > 0 && !selectedBusiness) {
        setSelectedBusiness(locationResponse.content[0].locationName);
      }
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  };

  // 선택된 사업장의 데이터 로드
  const loadBusinessData = async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      
      const selectedLocation = availableLocations.find(loc => loc.locationName === selectedBusiness);
      if (!selectedLocation) return;
      
      const locationDetail = await getLocationDetailApi(selectedLocation.id);
      console.log(`Loading data for business: ${selectedBusiness}`, locationDetail);
      
      setSelectedLocationData(locationDetail);
      
      const processedData = [];
      
      if (locationDetail.devices && locationDetail.devices.length > 0) {
        const activeDevices = locationDetail.devices.filter(device => device.status === 'ACTIVE');
        
        // 센서별 필터링
        let targetDevices = activeDevices;
        if (selectedSensor !== "종합") {
          const sensorNumber = parseInt(selectedSensor.replace("sensor", ""));
          targetDevices = activeDevices.filter(device => device.sensorNumber === sensorNumber);
        }
        
        // 각 기기의 센서 데이터 수집
        for (const device of targetDevices) {
          try {
            const deviceTableId = device.device?.id;
            console.log(`Trying to load data for device: ${device.deviceCode} (table ID: ${deviceTableId})`);
            
            let sensorData = [];
            
            // 1. 먼저 device.device.id (실제 device table ID)로 시도
            if (deviceTableId) {
              try {
                sensorData = await airQualityService.getDeviceSensorData(
                  deviceTableId,
                  startDate,
                  endDate
                );
                console.log(`Data found for device table ID ${deviceTableId}:`, sensorData.length);
              } catch (error) {
                console.log(`No data for device table ID ${deviceTableId}`);
              }
            }
            
            if (sensorData && sensorData.length > 0) {
              processedData.push(...sensorData.map(data => ({
                ...data,
                deviceCode: device.deviceCode,
                sensorNumber: device.sensorNumber
              })));
            }
          } catch (error) {
            console.error(`Failed to load data for device ${device.deviceCode}:`, error);
          }
        }
      }
      
      console.log(`Processed ${processedData.length} sensor readings for ${selectedBusiness}`);
      
      setBusinessData(processedData);
      generateChartDataFromReal(processedData);
      
    } catch (error) {
      console.error('Failed to load business data:', error);
      setChartData([]); // 에러 시에도 빈 차트 표시
      setBusinessData([]);
    } finally {
      setLoading(false);
    }
  };

  // 실제 데이터로부터 차트 데이터 생성
  const generateChartDataFromReal = (data: any[]) => {
    if (!data || data.length === 0) {
      setChartData([]); // 데이터가 없으면 빈 차트 표시
      return;
    }

    // 시간대별 평균 계산
    const hourlyData = {};
    
    data.forEach(item => {
      const time = new Date(item.measuredAt);
      const hour = time.getHours();
      const hourKey = hour.toString().padStart(2, "0") + "시";
      
      if (!hourlyData[hourKey]) {
        hourlyData[hourKey] = {
          time: hourKey,
          values: []
        };
      }
      
      // 메트릭별 값 계산
      let value;
      switch (selectedMetric) {
        case "복합악취":
          // 복합악취는 외부 API에서 추후 제공 예정으로 현재는 0으로 표기
          value = 0;
          break;
        case "황화수소":
          value = item.h2s || 0;
          break;
        case "암모니아":
          value = item.nh3 || 0;
          break;
        case "메탄가스":
          value = item.ch4 || 0;
          break;
        case "온도":
          value = item.temperature || 0;
          break;
        case "습도":
          value = item.humidity || 0;
          break;
        case "풍향":
          value = item.windDirection || 0;
          break;
        case "풍속":
          value = item.windSpeed || 0;
          break;
        default:
          value = 0;
      }
      
      hourlyData[hourKey].values.push(value);
    });
    
    // 평균 계산하여 차트 데이터 생성
    const chartData = Object.values(hourlyData).map((hourData: any) => {
      const avgValue = hourData.values.length > 0 
        ? hourData.values.reduce((sum, val) => sum + val, 0) / hourData.values.length
        : 0;
        
      return {
        time: hourData.time,
        value: Number(avgValue.toFixed(3))
      };
    }).sort((a, b) => parseInt(a.time) - parseInt(b.time));
    
    setChartData(chartData);
  };

  // 초기 로드
  useEffect(() => {
    loadAvailableLocations();
  }, []);

  // 데이터 로드 (selectedMetric 제거하여 탭 변경시 API 호출 방지)
  useEffect(() => {
    if (availableLocations.length > 0) {
      loadBusinessData();
    }
  }, [selectedBusiness, selectedSensor, startDate, endDate, availableLocations]);

  // 메트릭 변경시 기존 데이터로 차트 재생성 (API 호출 없음)
  useEffect(() => {
    if (businessData.length > 0) {
      generateChartDataFromReal(businessData);
    }
  }, [selectedMetric]);

  // 센서 목록 생성 (선택된 사업장의 기기 기반)
  const getAvailableSensors = () => {
    const sensors = ["종합"];
    if (selectedLocationData && selectedLocationData.devices) {
      const activeDevices = selectedLocationData.devices.filter(device => device.status === 'ACTIVE');
      const sensorNumbers = [...new Set(activeDevices.map(device => device.sensorNumber))].sort();
      sensorNumbers.forEach(num => {
        if (typeof num === 'number' && num >= 1 && num <= 5) {
          sensors.push(`sensor${num}`);
        }
      });
    }
    return sensors;
  };

  const generateChartData = () => {
    const data = [];
    for (let i = 0; i < 24; i++) {
      const hour = i.toString().padStart(2, "0");
      let value;

      switch (selectedMetric) {
        case "복합악취":
          value = 0.2 + Math.random() * 1.3;
          break;
        case "황화수소":
          value = 7.5 + Math.random() * 2;
          break;
        case "암모니아":
          value = 0.5 + Math.random() * 1.5;
          break;
        case "메탄가스":
          value = 100 + Math.random() * 60;
          break;
        case "온도":
          value = 25 + Math.random() * 5;
          break;
        case "습도":
          value = 0;
          break;
        case "풍향":
          value = Math.random() * 360;
          break;
        case "풍속":
          value = 0.5 + Math.random() * 0.5;
          break;
      }

      data.push({
        time: `${hour}시`,
        value: value,
      });
    }
    setChartData(data);
  };

  const handleExcelDownload = () => {
    console.log("엑셀 다운로드");
  };

  // CustomTooltip 컴포넌트에 타입 추가
  interface CustomTooltipProps {
    active?: boolean;
    payload?: any[];
    label?: string;
  }

// 차트 커스텀 툴팁
  const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
          <div className="chart-tooltip">
            <p style={{ margin: 0, whiteSpace: 'pre-line', color: 'white' }}>
              {`${label}\n값: ${payload[0].value.toFixed(2)}`}
            </p>
          </div>
      );
    }
    return null;
  };

  return (
    <div className="device-list-container">
      {/* 탭 버튼 */}
      <div className="tab-buttons">
        <div className="" style={{ float: "left" }}>
          <button
            className="tab-button"
            onClick={() => {
              window.location.href = "/stat";
            }}
          >
            나주시 시간대별 종합수치
          </button>
          <button
            className="tab-button"
            onClick={() => {
              window.location.href = "/stat/region";
            }}
          >
            지역 시간대별 종합수치
          </button>
          <button
            className="tab-button active"
            onClick={() => {
              window.location.href = "/stat/location";
            }}
          >
            사업장 시간대별 종합수치
          </button>
        </div>
        <a
          href={"javascript:;"}
          className="btn-excel-download"
          onClick={handleExcelDownload}
          style={{ float: "right" }}
        >
          <img src={"/img/btn_excel01.png"} />
        </a>
      </div>

      <div className="dlv_dv01">
        <div className="stat-location-content">
          {/* 왼쪽 사업장 메뉴 */}
          <div className="business-sidebar">
            <div className="business-select-wrapper">
              <select className="business-select" disabled>
                <option value="나주시">나주시</option>
              </select>
            </div>

            <div className="business-list">
              <div>
                <div className="business-category-header">
                  <span>사업장</span>
                </div>
                <ul className="business-items">
                  {availableLocations.map((location) => (
                    <li
                      key={location.id}
                      className={selectedBusiness === location.locationName ? "active" : ""}
                      onClick={() => setSelectedBusiness(location.locationName)}
                    >
                      {location.locationName}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* 오른쪽 콘텐츠 영역 */}
          <div className="stat-content-right">
            <div className="env-content">
              {/* 사업장명 표시 */}
              <div className="env-dv01">
              <div className="business-title">
                <span className="business-dot">●</span> {selectedBusiness}
                <span className="business-subtitle">
                  {selectedLocationData ? ` ${selectedLocationData.fullAddress || ''}` : ''}
                </span>
              </div>

              {/* 센서 선택 버튼 */}
              <div className="sensor-buttons">
                {getAvailableSensors().map((sensor) => (
                  <button
                    key={sensor}
                    className={`sensor-btn ${selectedSensor === sensor ? "active" : ""}`}
                    onClick={() => setSelectedSensor(sensor)}
                  >
                    {sensor}
                  </button>
                ))}
              </div>
              </div>

              <div className="date-selector">
                <span>조회일</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="date-input"
                />
                <span className="date-separator">~</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="date-input"
                />
              </div>

              <div className="table-section">
                <table className="device-table">
                  <thead>
                    <tr>
                      <th>측정일자</th>
                      <th>측정시간</th>
                      <th>복합악취(배수)</th>
                      <th>황화수소(ppm)</th>
                      <th>암모니아(ppm)</th>
                      <th>메탄가스(ppm)</th>
                      <th>온도</th>
                      <th>습도</th>
                      <th>풍향</th>
                      <th>풍속</th>
                      <th>등급</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={11}>데이터 로딩 중...</td>
                      </tr>
                    ) : businessData.length > 0 ? (
                      businessData.map((data, i) => {
                        const date = new Date(data.measuredAt);
                        return (
                          <tr key={i}>
                            <td>{date.toLocaleDateString('ko-KR')}</td>
                            <td>{date.getHours().toString().padStart(2, '0')}시</td>
                            <td>0</td>
                            <td>{(data.h2s || 0).toFixed(2)}</td>
                            <td>{(data.nh3 || 0).toFixed(2)}</td>
                            <td>{(data.ch4 || 0).toFixed(2)}</td>
                            <td>{(data.temperature || 0).toFixed(1)}°C</td>
                            <td>{(data.humidity || 0).toFixed(1)}%</td>
                            <td>-</td>
                            <td>-</td>
                            <td>{data.compositeGrade || "좋음"}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={11}>{selectedBusiness ? "해당 사업장에 데이터가 없습니다." : "사업장을 선택해주세요."}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="metric-buttons">
                {["복합악취", "황화수소", "암모니아", "메탄가스", "온도", "습도", "풍향", "풍속"].map(
                  (metric) => (
                    <button
                      key={metric}
                      className={`metric-btn ${selectedMetric === metric ? "active" : ""}`}
                      onClick={() => setSelectedMetric(metric)}
                    >
                      {metric}
                    </button>
                  )
                )}
              </div>

              <div className="chart-container">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="time" stroke="#666" tick={{ fill: "#999", fontSize: 12 }} />
                      <YAxis
                        stroke="#666"
                        tick={{ fill: "#999", fontSize: 12 }}
                        domain={[0, "dataMax + 1"]}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#1dd3d6"
                        strokeWidth={2}
                        dot={{ fill: "#1dd3d6", r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ 
                    height: 300, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    color: '#666',
                    fontSize: '16px'
                  }}>
                    표시할 데이터가 없습니다.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatLocationView;
